import React, { FC, useMemo } from 'react';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import AnomalyRecommendation from './anomaly-recommendation';
import CustomPDFExport from '../../blocks/pdf-export';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomDamageClass from '../custom-general/custom-damage-class';
import DamageClassSummary from './damage-class-summary';
import EquipmentAnalytics from './report-items/equipmentAnalytics';
import ExecutiveSummary from './executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface FVIEquipmentReportProps {
	certificates?: ICertificate;
	coverData: models.CustomFVIData;
	projectDetails: models.Project;
	equipmentItems: models.FVIEquipmentItem[];
	includeSummary: models.IncludeSummary;
	reportTitle: string;
	executiveSummary: string;
}

const FVIEquipmentReport: FC<FVIEquipmentReportProps> = (props) => {
	const { inspectionTypeName, organisationName } = props.projectDetails;
	const overview = useCoverSheetOverview();

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: `4. ${organisationName} Work Procedure (Equipment & Vessel).`,
			value: props.coverData?.exxonMobilWorkProcedure,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const executiveSummary = useMemo(() => {
		if (props.includeSummary === 'include') {
			return props.executiveSummary + utils.getExecutiveSummaryAffix(props.equipmentItems, 'equipment');
		}

		return props.executiveSummary;
	}, [props.equipmentItems, props.includeSummary, props.executiveSummary]);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<CustomDamageClass />

			<ExecutiveSummary summary={executiveSummary} />

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			<DamageClassSummary
				items={props.equipmentItems}
				itemNumberKey='equipmentNumber'
				columns={damageClassColumns}
			/>

			<CustomPDFExport>
				{(params) => (
					<AnomalyRecommendation 
						projectDetails={props.projectDetails} 
						items={props.equipmentItems}
						exportParams={params}
					/>
				)}
			</CustomPDFExport>

			<EquipmentAnalytics includeEmptyPDONotice includePDO items={props.equipmentItems} useGroupedTitle />
		</React.Fragment>
	);
};

const damageClassColumns: models.Column[] = [
	{
		path: 'equipmentNumber',
		label: 'Equipment Tag',
	},
	{
		path: 'equipmentDescription',
		label: 'Equipment Description',
	},
];

export default withPDF(FVIEquipmentReport);
